import React, { useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Maximize2, Minimize2 } from "lucide-react";
import { BeraterFormField } from "./BeraterFormField";
import { DraggableRankingList } from "./DraggableRankingList";
import { PrioritySettingsForm } from "./PrioritySettingsForm";
import { PropertyPlanningForm } from "./PropertyPlanningForm";
import { ChildPlanningForm } from "./ChildPlanningForm";
import { HealthPlanningForm } from "./HealthPlanningForm";
import { RetirementPlanningForm } from "./RetirementPlanningForm";
import { InvestmentForm } from "./InvestmentForm";
import { HouseholdBudgetForm } from "./HouseholdBudgetForm";
import { PropertyInsuranceForm } from "./PropertyInsuranceForm";
import { IncomeProtectionForm } from "./IncomeProtectionForm";
import { ReferralFeedbackForm } from "./ReferralFeedbackForm";
import { useFullscreen } from "@/hooks/useFullscreen";
import { cn } from "@/lib/utils";
import type { BeratungSeite } from "@/hooks/useBeratungSeiten";

interface BeraterPanelProps {
  seite: BeratungSeite | null;
  formValues: Record<string, any>;
  onFieldChange: (fieldId: string, value: any) => void;
}

export function BeraterPanel({ seite, formValues, onFieldChange }: BeraterPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const { isFullscreen, toggleFullscreen } = useFullscreen(panelRef);
  
  if (!seite) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        Keine Seite ausgewählt
      </div>
    );
  }

  const layout = seite.berater_layout;
  const kundenLayout = seite.kunden_layout;
  const rankingKey = `ranking_${seite.id}`;

  // Check if this is a priority-tiles page (new layout)
  const isPriorityTilesPage = kundenLayout?.type === 'priority-tiles';
  
  // Check if this is a rent-timeline page (Eigentum schaffen)
  const isRentTimelinePage = kundenLayout?.type === 'rent-timeline';
  
  // Check if this is a child-provision page (Kindervorsorge)
  const isChildProvisionPage = kundenLayout?.type === 'child-provision';
  
  // Check if this is a benefit-checklist page (Gesundheit)
  const isBenefitChecklistPage = kundenLayout?.type === 'benefit-checklist';
  
  // Check if this is a retirement-planning page (Altersvorsorge)
  const isRetirementPlanningPage = kundenLayout?.type === 'retirement-planning';
  
  // Check for new page types
  const isInvestmentPage = kundenLayout?.type === 'investment-view';
  const isHouseholdBudgetPage = kundenLayout?.type === 'household-budget';
  const isPropertyInsurancePage = kundenLayout?.type === 'property-insurance';
  const isIncomeProtectionPage = kundenLayout?.type === 'income-protection';
  const isReferralFeedbackPage = kundenLayout?.type === 'referral-feedback';

  const renderFormContent = () => {
    // For priority-tiles pages, render the priority settings form
    if (isPriorityTilesPage) {
      return (
        <PrioritySettingsForm
          formValues={formValues}
          onFieldChange={onFieldChange}
        />
      );
    }
    
    // For rent-timeline pages (Eigentum schaffen), render the property planning form
    if (isRentTimelinePage) {
      return (
        <PropertyPlanningForm
          formValues={formValues}
          onFieldChange={onFieldChange}
        />
      );
    }
    
    // For child-provision pages (Kindervorsorge), render the child planning form
    if (isChildProvisionPage) {
      return (
        <ChildPlanningForm
          formValues={formValues}
          onFieldChange={onFieldChange}
        />
      );
    }
    
    // For benefit-checklist pages (Gesundheit), render the health planning form
    if (isBenefitChecklistPage) {
      return (
        <HealthPlanningForm
          formValues={formValues}
          onFieldChange={onFieldChange}
        />
      );
    }
    
    // For retirement-planning pages (Altersvorsorge), render the retirement planning form
    if (isRetirementPlanningPage) {
      return (
        <RetirementPlanningForm
          formValues={formValues}
          onFieldChange={onFieldChange}
        />
      );
    }
    
    // New module forms
    if (isInvestmentPage) {
      return <InvestmentForm formValues={formValues} onFieldChange={onFieldChange} />;
    }
    if (isHouseholdBudgetPage) {
      return <HouseholdBudgetForm formValues={formValues} onFieldChange={onFieldChange} />;
    }
    if (isPropertyInsurancePage) {
      return <PropertyInsuranceForm formValues={formValues} onFieldChange={onFieldChange} />;
    }
    if (isIncomeProtectionPage) {
      return <IncomeProtectionForm formValues={formValues} onFieldChange={onFieldChange} />;
    }
    if (isReferralFeedbackPage) {
      return <ReferralFeedbackForm formValues={formValues} onFieldChange={onFieldChange} />;
    }

    if (layout.type === 'form' && layout.fields) {
      return (
        <div className="space-y-4">
          {layout.fields.map((field) => (
            <BeraterFormField
              key={field.id}
              field={field}
              value={formValues[field.id]}
              onChange={(value) => onFieldChange(field.id, value)}
              formValues={formValues}
            />
          ))}
        </div>
      );
    }

    if (layout.type === 'ranking' && layout.items) {
      return (
        <DraggableRankingList
          items={layout.items}
          value={formValues[rankingKey]}
          onChange={(orderedIds) => onFieldChange(rankingKey, orderedIds)}
        />
      );
    }

    if (layout.type === 'checklist' && layout.items) {
      return (
        <div className="space-y-3">
          {layout.items.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-3 p-3 border rounded-lg"
            >
              <input
                type="checkbox"
                id={item.id}
                checked={!!formValues[item.id]}
                onChange={(e) => onFieldChange(item.id, e.target.checked)}
                className="h-4 w-4 rounded border-primary text-primary focus:ring-primary"
              />
              <label htmlFor={item.id} className="flex-1 cursor-pointer">
                {item.label}
              </label>
            </div>
          ))}
        </div>
      );
    }

    if (layout.type === 'goals' && layout.fields) {
      return (
        <div className="space-y-4">
          {layout.fields.map((field) => (
            <BeraterFormField
              key={field.id}
              field={field}
              value={formValues[field.id]}
              onChange={(value) => onFieldChange(field.id, value)}
              formValues={formValues}
            />
          ))}
        </div>
      );
    }

    return (
      <p className="text-muted-foreground">
        Layout-Typ "{layout.type}" wird noch implementiert.
      </p>
    );
  };

  return (
    <div 
      ref={panelRef} 
      className={cn(
        "h-full flex flex-col",
        isFullscreen && "bg-background"
      )}
    >
      <div className="p-4 border-b flex items-center justify-between">
        <h2 className="font-semibold">Berater-Ansicht</h2>
        <Button
          variant="outline"
          size="icon"
          onClick={toggleFullscreen}
          className="h-8 w-8"
          title={isFullscreen ? "Vollbild beenden" : "Vollbild"}
        >
          {isFullscreen ? (
            <Minimize2 className="h-4 w-4" />
          ) : (
            <Maximize2 className="h-4 w-4" />
          )}
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {(isPriorityTilesPage || isRentTimelinePage || isChildProvisionPage || isBenefitChecklistPage || isRetirementPlanningPage || isInvestmentPage || isHouseholdBudgetPage || isPropertyInsurancePage || isIncomeProtectionPage || isReferralFeedbackPage) ? (
          // Special pages get the form directly without card wrapper
          <div className="space-y-4">
            {renderFormContent()}
          </div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{seite.titel}</CardTitle>
              {seite.untertitel && (
                <CardDescription>{seite.untertitel}</CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {renderFormContent()}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
